import React, { useState } from 'react';
import { FiSend, FiMail, FiUsers, FiTrendingUp } from 'react-icons/fi';
import { sendPromotionalEmail } from '../services/newsletter';
import toast from 'react-hot-toast';

export default function PromotionalEmails() {
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    target: 'all_subscribers', // all_subscribers, recent_customers, all_customers
    sendTo: 'active'
  });
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.content) {
      toast.error('Please fill in subject and content');
      return;
    }
    setSending(true);
    try {
      const data = await sendPromotionalEmail(formData);
      toast.success(data.message);
      setFormData({ subject: '', content: '', target: 'all_subscribers', sendTo: 'active' });
    } catch (error) {
      toast.error('Failed to send promotional emails');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Promotional Emails</h1>
        <button
          onClick={() => setPreview(!preview)}
          className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
        >
          <FiMail className="mr-2" /> {preview ? 'Edit' : 'Preview'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Special Offer: 20% off on all tours!"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                <select
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="all_subscribers">All Newsletter Subscribers</option>
                  <option value="recent_customers">Recent Customers (Last 30 days)</option>
                  <option value="all_customers">All Customers (who opted in)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content (HTML) *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  placeholder={`<h2>Special Offer!</h2>
<p>Get 20% off on all desert safari tours. Use code: SAFARI20</p>
<a href="https://redsearoad.tours/services" style="background: #006994; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Book Now</a>`}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
                <select
                  value={formData.sendTo}
                  onChange={(e) => setFormData({ ...formData, sendTo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="active">Active Subscribers Only</option>
                  <option value="all">All Subscribers (including unsubscribed)</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={sending}
                className="w-full bg-primary-500 text-white py-3 rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {sending ? <FiSend className="animate-spin" /> : <FiSend />}
                {sending ? 'Sending...' : 'Send Promotional Email'}
              </button>
            </div>
          </form>
        </div>

        {/* Info Card */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Tips for effective emails</h3>
            <ul className="space-y-3 text-sm text-gray-600">
              <li>✓ Use an engaging subject line</li>
              <li>✓ Personalize the content</li>
              <li>✓ Include a clear call-to-action</li>
              <li>✓ Add images to make it attractive</li>
              <li>✓ Keep it mobile-friendly</li>
              <li>✓ Include unsubscribe link</li>
            </ul>
          </div>

          <div className="bg-primary-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-primary-800 mb-2">Email Variables</h3>
            <p className="text-sm text-primary-600 mb-3">Use these variables in your content:</p>
            <code className="block text-xs bg-white p-2 rounded">
              {'{{customer_name}}'} - Customer's name<br />
              {'{{unsubscribe_url}}'} - Unsubscribe link<br />
              {'{{year}}'} - Current year
            </code>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">Email Preview</h2>
              <button onClick={() => setPreview(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-6">
              <div className="border rounded-lg p-4">
                <div dangerouslySetInnerHTML={{ 
                  __html: formData.content 
                    .replace('{{customer_name}}', '<span class="bg-yellow-100">[Customer Name]</span>')
                    .replace('{{unsubscribe_url}}', '<span class="bg-yellow-100">[Unsubscribe Link]</span>')
                    .replace('{{year}}', new Date().getFullYear())
                }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
